import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { QuotationService } from '../../services/quotation.service';
import { QuotationLineCreateDTO } from '../../interfaces/sales/quotation.interface';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { SalesSettings } from '../../interfaces/sales/salesSettings.interface';
import { AccountService } from '../../services/account.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const today = () => new Date().toISOString().slice(0, 10);

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// In-state row model: the DTO fields (quantity/unitPrice/taxRatePct may be NaN
// while the user is typing) plus a stable client-side key that is never sent
// to the server.
interface LineRow extends Omit<QuotationLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: string;
  quantity: number;   // may be NaN while field is empty
  unitPrice: number;  // may be NaN while field is empty
  taxRatePct: number; // may be NaN while field is empty
}

// Helpers that treat NaN as 0 for live-total computation only.
const safeLineNet = (row: LineRow): number => {
  const q = Number.isFinite(row.quantity) ? row.quantity : 0;
  const p = Number.isFinite(row.unitPrice) ? row.unitPrice : 0;
  return +((q * p)).toFixed(4);
};
const safeLineTax = (row: LineRow): number => {
  const t = Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0;
  return +((safeLineNet(row) * t) / 100).toFixed(4);
};

const QuotationCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  // Stable key counter — bumped each time a new row is created.
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  // Header state
  const [customerId, setCustomerId] = useState('');
  const [quotationDate, setQuotationDate] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');

  // Reference data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [settings, setSettings] = useState<SalesSettings | null>(null);

  // Lines — stored as LineRow[] (includes transient key)
  const [lines, setLines] = useState<LineRow[]>([]);

  const [validUntilTouched, setValidUntilTouched] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const makeBlankLine = (s: SalesSettings | null): LineRow => ({
    key: nextKey(),
    description: '',
    quantity: 1,
    unitPrice: NaN,
    taxRatePct: s?.defaultTaxRatePct ?? 0,
    revenueAccountId: undefined,
    segmentId: undefined,
  });

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!;
    const dataLoads: [
      Promise<any>,
      Promise<SalesSettings | null>,
      Promise<Account[]>,
      Promise<any>,
    ] = [
      CustomerService.listByInstitution(institutionId, 0, 200),
      SalesSettingsService.getByInstitution(institutionId).catch(() => null),
      AccountService.getAccountsByType('REVENUE'),
      SegmentService.getByInstitution(0, 200),
    ];

    if (isEditMode) {
      Promise.all([
        ...dataLoads,
        QuotationService.getById(id!),
      ])
        .then(([customerPage, salesSettings, revenueAccs, segmentPage, quotation]) => {
          setCustomers(customerPage.content);
          setSettings(salesSettings);

          const allAccounts: Account[] = Array.isArray(revenueAccs) ? revenueAccs : [];
          setRevenueAccounts(allAccounts);

          const allSegments: Segment[] = segmentPage.content ?? [];
          setSegments(allSegments.filter((s: Segment) => s.active !== false));

          // Prefill from existing quotation
          setCustomerId(quotation.customerId);
          setQuotationDate(quotation.quotationDate);
          setValidUntil(quotation.validUntil ?? '');
          setNotes(quotation.notes ?? '');

          // Prefill lines with stable keys
          setLines(
            (quotation.lines ?? []).map((l: any) => ({
              key: nextKey(),
              description: l.description ?? '',
              quantity: l.quantity,
              unitPrice: l.unitPrice,
              taxRatePct: l.taxRatePct,
              revenueAccountId: l.revenueAccountId ?? undefined,
              segmentId: l.segmentId ?? undefined,
            }))
          );
        })
        .catch(e => toast.showApiError(e, 'Failed to load quotation data'))
        .finally(() => setLoading(false));
    } else {
      Promise.all(dataLoads)
        .then(([customerPage, salesSettings, revenueAccs, segmentPage]) => {
          setCustomers(customerPage.content);
          setSettings(salesSettings);

          const allAccounts: Account[] = Array.isArray(revenueAccs) ? revenueAccs : [];
          setRevenueAccounts(allAccounts);

          const allSegments: Segment[] = segmentPage.content ?? [];
          setSegments(allSegments.filter((s: Segment) => s.active !== false));

          // Prefill validUntil from settings if available
          if (salesSettings?.defaultQuotationValidityDays) {
            setValidUntil(addDays(today(), salesSettings.defaultQuotationValidityDays));
          }

          // Start with one blank line
          setLines([makeBlankLine(salesSettings)]);
        })
        .catch(e => toast.showApiError(e, 'Failed to load quotation data'))
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuotationDateChange = (newDate: string) => {
    setQuotationDate(newDate);
    // Re-derive validUntil from the new quotation date if settings provide a default
    // and user has not yet set a custom validUntil (only in create mode)
    if (!isEditMode && settings?.defaultQuotationValidityDays && !validUntilTouched) {
      setValidUntil(addDays(newDate, settings.defaultQuotationValidityDays));
    }
  };

  const handleAddLine = () => {
    setLines(prev => [...prev, makeBlankLine(settings)]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = <K extends keyof LineRow>(
    index: number,
    field: K,
    value: LineRow[K]
  ) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Live totals — NaN fields treated as 0.
  const subtotal = lines.reduce((s, l) => s + safeLineNet(l), 0);
  const taxAmount = lines.reduce((s, l) => s + safeLineTax(l), 0);
  const total = subtotal + taxAmount;

  const isSubmitDisabled =
    saving ||
    !customerId ||
    lines.length === 0 ||
    lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0));

  const handleSubmit = async () => {
    if (!customerId) { toast.showApiError(null, 'Select a customer'); return; }
    if (lines.length === 0) { toast.showApiError(null, 'Add at least one line'); return; }
    if (lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive quantity and unit price');
      return;
    }

    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution()!;
      const dto = {
        customerId,
        quotationDate,
        validUntil: validUntil || undefined,
        notes: notes || undefined,
        institutionId,
        // Strip the transient key; coerce taxRatePct NaN→0 (qty/price are
        // finite because the submit guard above already checked > 0).
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
        })),
      };

      if (isEditMode) {
        await QuotationService.update(id!, dto);
        toast.showSuccess('Quotation updated');
        navigate(`/sales/quotations/${id}`);
      } else {
        const created = await QuotationService.create(dto);
        toast.showSuccess('Quotation created');
        navigate(`/sales/quotations/${created.id}`);
      }
    } catch (e) {
      toast.showApiError(e, isEditMode ? 'Failed to update Quotation' : 'Failed to create Quotation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEditMode ? 'Edit Quotation' : 'New Quotation'}
      </Typography>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Customer"
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
            required
          >
            {customers.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Quotation Date"
            type="date"
            value={quotationDate}
            onChange={e => handleQuotationDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Valid Until"
            type="date"
            value={validUntil}
            onChange={e => { setValidUntilTouched(true); setValidUntil(e.target.value); }}
            InputLabelProps={{ shrink: true }}
            helperText="Defaults to settings validity if left blank"
          />

          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />
        </Box>
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Quotation Lines</Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddLine}>
            Add Line
          </Button>
        </Box>

        {lines.length === 0 ? (
          <Typography color="text.secondary">No lines added yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Tax %</TableCell>
                <TableCell>Revenue Account</TableCell>
                <TableCell>Segment</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.key}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.description}
                      onChange={e => handleLineChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.quantity) ? line.quantity : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'quantity', v === '' ? NaN : Number(v));
                      }}
                      error={!(line.quantity > 0)}
                      inputProps={{ step: 1, min: 0 }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'unitPrice', v === '' ? NaN : Number(v));
                      }}
                      error={!(line.unitPrice > 0)}
                      inputProps={{ step: 0.0001, min: 0 }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'taxRatePct', v === '' ? NaN : Number(v));
                      }}
                      inputProps={{ step: 0.01, min: 0 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 180 }}>
                      <AccountAutocomplete
                        accounts={revenueAccounts}
                        value={line.revenueAccountId ?? ''}
                        label="Revenue Account"
                        placeholder="—"
                        onChange={accountId => {
                          handleLineChange(index, 'revenueAccountId', accountId || undefined);
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Segment</InputLabel>
                      <Select
                        label="Segment"
                        value={line.segmentId ?? ''}
                        onChange={e => {
                          const val = e.target.value as string;
                          handleLineChange(index, 'segmentId', val || undefined);
                        }}
                      >
                        <MenuItem value="">—</MenuItem>
                        {segments.map(s => (
                          <MenuItem key={s.id} value={s.id!}>{s.code} — {s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">{safeLineNet(line).toFixed(4)}</TableCell>
                  <TableCell align="right">{safeLineTax(line).toFixed(4)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveLine(index)}
                      aria-label="remove line"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body1">{subtotal.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body1">{taxAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6">{total.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {saving ? <CircularProgress size={20} /> : isEditMode ? 'Update Quotation' : 'Create Quotation'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default QuotationCreate;
